import { Component, Input, OnInit, ViewChild } from '@angular/core'
import {
  AppSelectComponent,
  GridSize,
  GridWrapperComponent,
  MultiRowSelection,
  Option,
  SingleRowSelection
} from 'common-components'
import {
  FeeScheduleODataService,
  IFeeSchedule,
  IProcedureCode,
  IProcedureCodeFee,
  ProcedureCodeFeeODataService,
  ProcedureCodeODataService,
  ProcedureCodesetODataService
} from '@exchange/domain/.'
import { forkJoin } from 'rxjs'
import { ColDef } from 'ag-grid-community'
import { ToastrService } from 'ngx-toastr'
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal'

@Component({
  selector: 'app-fee-schedule-edit-codes',
  templateUrl: './fee-schedule-edit-codes.component.html',
  standalone: false
})
export class FeeScheduleEditCodesComponent implements OnInit {
  @ViewChild('available', { static: true }) available: GridWrapperComponent
  @ViewChild('selected', { static: true }) selected: GridWrapperComponent
  @ViewChild('codeSetList', { static: true }) codeSetList: AppSelectComponent

  @Input() public set feeScheduleId(value: number) {
    this._feeScheduleId = value
    this.selected.defaultFilter = `FeeScheduleId eq ${value}`
  }

  private _gridOptions = {
    canEdit: false,
    canDelete: false
  }
  private _feeScheduleId: number
  codeSets: Option[] = []
  private selectedColumnDefinitions: ColDef[] = [
    {
      headerName: 'Code',
      field: 'Code',
      width: 75,
      cellRenderer: GridWrapperComponent.TooltipRenderer(),
      filter: 'agTextColumnFilter',
      filterParams: { newRowsAction: 'keep' }
    },
    {
      headerName: 'Modifier',
      field: 'Modifier',
      width: 100,
      editable: true,
      cellRenderer: GridWrapperComponent.TooltipRenderer(),
      filter: 'agTextColumnFilter',
      filterParams: { newRowsAction: 'keep' },
      cellStyle: { 'background-color': 'lightBlue' }
    },
    {
      headerName: 'Pricing Method',
      width: 100,
      valueGetter: params => {
        return params.data.Amount == null ? '% Billed Charges' : 'Specific Amount'
      },
      valueSetter: params => {
        params.data.Amount = params.newValue === 'Specific Amount' ? 0.0 : null
        params.data.PercentageBilled = params.newValue === '% Billed Charges' ? 100 : null
        return true
      },
      editable: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: ['Specific Amount', '% Billed Charges']
      }
    },
    {
      headerName: 'Amount',
      valueGetter: params => {
        return params.data.Amount
      },
      valueSetter: params => {
        if (params.newValue !== null && params.newValue < 0) {
          this.toastr.error('Amount must be 0 or greater')
          return false
        }

        params.data.Amount = params.newValue
      },
      width: 100,
      editable: params => params.data.Amount !== null,
      cellRenderer: GridWrapperComponent.CurrencyRenderer(),
      filter: 'agNumberColumnFilter',
      filterParams: { newRowsAction: 'keep' },
      cellStyle: { 'background-color': 'lightBlue' }
    },
    {
      headerName: 'Percentage Billed',
      valueGetter: params => {
        return params.data.PercentageBilled
      },
      valueSetter: params => {
        if (params.newValue !== null && (params.newValue < 0 || params.newValue > 100)) {
          this.toastr.error('Percentage Billed must be between 0 and 100')
          return false
        }

        params.data.PercentageBilled = params.newValue
        return true
      },
      width: 100,
      editable: params => params.data.PercentageBilled !== null,
      cellRenderer: GridWrapperComponent.PercentRenderer(),
      filter: 'agNumberColumnFilter',
      filterParams: { newRowsAction: 'keep' },
      cellStyle: { 'background-color': 'lightBlue' }
    },
    {
      headerName: 'Description',
      field: 'Description',
      width: 200,
      cellRenderer: GridWrapperComponent.TooltipRenderer(),
      filter: 'agTextColumnFilter',
      filterParams: { newRowsAction: 'keep' }
    }
  ]

  availableSelection: IProcedureCode[] = []
  selectedSelection: IProcedureCodeFee[] = []

  constructor(
    private procedureCodeSetService: ProcedureCodesetODataService,
    private procedureCodeService: ProcedureCodeODataService,
    private procedureCodeFeeService: ProcedureCodeFeeODataService,
    private toastr: ToastrService,
    private modalService: BsModalService
  ) {}

  ngOnInit() {
    this.procedureCodeSetService
      .query()
      .orderBy('Name')
      .exec()
      .subscribe(response => {
        this.codeSets = response.data.map(x => new Option(x.Id, x.Name, x))
      })

    this.initializeAvailable()
    this.initializeSelected()

    this.selected.defaultFilter = `FeeScheduleId eq ${this._feeScheduleId}`
    this.selected.getData()
  }

  initializeAvailable() {
    this.available.gridOptions.context = this._gridOptions
    this.available.gridOptions.columnDefs = [
      {
        headerName: 'Value',
        field: 'Px',
        width: 75,
        cellRenderer: GridWrapperComponent.TooltipRenderer(),
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' }
      },
      {
        headerName: 'Description',
        field: 'Description',
        width: 300,
        cellRenderer: GridWrapperComponent.TooltipRenderer(),
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' }
      }
    ]
    this.available.gridOptions.rowSelection = MultiRowSelection
    this.available.odata = this.procedureCodeService
    this.available.gridOptions.onSelectionChanged = this.recalculateSelected.bind(this)
  }

  initializeSelected() {
    this.selected.defaultFilter = `FeeScheduleId eq null`
    this.selected.gridOptions.context = this._gridOptions
    this.selected.gridOptions.columnDefs = this.selectedColumnDefinitions
    this.selected.gridOptions.rowSelection = MultiRowSelection
    this.selected.odata = this.procedureCodeFeeService
    this.selected.gridOptions.onSelectionChanged = this.recalculateSelected.bind(this)
  }

  fieldChanged(e): void {
    // TODO: Figure out why e.value.Amount is a string!
    // Current guess is that when the user types a new value for Amount (this field is editable)
    // it's picked up as a string and not coerced back into a number.  Evidence is that if you
    // edit the 'Modifier' field then Amount is a number here.
    e.data.Amount = e.data.Amount === null ? null : +e.data.Amount
    e.data.PercentageBilled = e.data.PercentageBilled === null ? null : +e.data.PercentageBilled

    this.procedureCodeFeeService
      .put(e.data, e.data.Id)
      .exec()
      .subscribe(result => {
        this.selected.getData()
      })
  }

  recalculateSelected() {
    this.availableSelection = this.available.api.getSelectedRows() as IProcedureCode[]

    this.selectedSelection = this.selected.api.getSelectedRows() as IProcedureCodeFee[]
  }

  select() {
    // TODO: Implement ODATA Batch
    const observables = this.availableSelection.map(item => {
      const model = {
        Code: item.Px,
        FeeScheduleId: this._feeScheduleId,
        Amount: 0,
        Description: item.Description,
        Modifier: '',
        PercentageBilled: null
      } as IProcedureCodeFee

      return this.procedureCodeFeeService.post(model).exec()
    })

    forkJoin(observables).subscribe(results => {
      this.selected.getData()
      setTimeout(this.recalculateSelected.bind(this), 250)
    })
  }

  remove() {
    // TODO: Implement ODATA Batch
    const observables = this.selectedSelection.map(item =>
      this.procedureCodeFeeService.delete(item.Id).exec()
    )

    forkJoin(observables).subscribe(results => {
      this.selected.getData()
      setTimeout(this.recalculateSelected.bind(this), 250)
    })
  }

  doCopyFrom = (copyFromSelection: IFeeSchedule, callback: () => void) => {
    this.procedureCodeFeeService
      .query()
      .filter(`FeeScheduleId eq ${copyFromSelection.Id}`)
      .exec()
      .subscribe(source => {
        // TODO: Implement ODATA Batch
        const observables = source.data.map(item => {
          const model = {
            Code: item.Code,
            FeeScheduleId: this._feeScheduleId,
            Amount: item.Amount,
            Description: item.Description,
            Modifier: item.Modifier,
            PercentageBilled: item.PercentageBilled
          } as IProcedureCodeFee

          return this.procedureCodeFeeService.post(model).exec()
        })

        forkJoin(observables).subscribe(results => {
          this.selected.getData()
          callback()
          setTimeout(this.recalculateSelected.bind(this), 250)
        })
      })
  }

  importClicked() {
    this.modalService.show(FeeScheduleEditCodesCopyModalComponent, {
      initialState: {
        doCopyFrom: this.doCopyFrom
      },
      class: 'modal-lg'
    })
  }
}

@Component({
  templateUrl: './fee-schedule-edit-codes-copy-modal.component.html',
  standalone: false
})
export class FeeScheduleEditCodesCopyModalComponent implements OnInit {
  @ViewChild('copyFromGrid', { static: true }) copyFromGrid: GridWrapperComponent
  GridSize = GridSize
  copyFromSelection: IFeeSchedule = null
  copyLoading = false

  doCopyFrom: (copyFromSelection: IFeeSchedule, callback: () => void) => void

  constructor(public modalRef: BsModalRef, private odata: FeeScheduleODataService) {}

  ngOnInit(): void {
    this.initializeCopyFromGrid()
  }

  initializeCopyFromGrid() {
    this.copyFromGrid.gridOptions.context = {
      canEdit: false,
      canDelete: false
    }
    this.copyFromGrid.gridOptions.columnDefs = [
      {
        headerName: 'Name',
        field: 'Name',
        width: 300,
        cellRenderer: GridWrapperComponent.TooltipRenderer(),
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' }
      },
      {
        headerName: 'Active On',
        field: 'ActiveOnDateTime',
        width: 100,
        cellRenderer: GridWrapperComponent.DateRenderer(),
        filter: 'agDateColumnFilter',
        filterParams: { newRowsAction: 'keep' }
      },
      {
        headerName: 'Expires On',
        field: 'ExpiresOnDateTime',
        width: 100,
        cellRenderer: GridWrapperComponent.DateRenderer(),
        filter: 'agDateColumnFilter',
        filterParams: { newRowsAction: 'keep' }
      }
    ]
    this.copyFromGrid.gridOptions.rowSelection = SingleRowSelection
    this.copyFromGrid.gridOptions.onSelectionChanged = () => {
      this.copyFromSelection = this.copyFromGrid.api.getSelectedRows()[0]
    }
    this.copyFromGrid.odata = this.odata
  }

  copy = () => {
    this.copyLoading = true
    this.doCopyFrom(this.copyFromSelection, () => {
      this.copyLoading = false
      this.modalRef.hide()
    })
  }
}
